// Note: This is a frontend service that communicates with Stripe via Supabase Edge Functions
// The actual Stripe SDK operations will be handled server-side for security

import supabase from './supabase';
import subscriptionService from './subscriptionService';

class StripeService {
  /**
   * Create a Stripe checkout session for subscription upgrade
   * @param {string} organizationId - The organization ID
   * @param {string} planId - The plan ID to upgrade to
   * @param {string} billingCycle - 'monthly' or 'yearly'
   * @returns {Promise<{success: boolean, data?: object, error?: string}>}
   */
  async createCheckoutSession(organizationId, planId, billingCycle = 'monthly') {
    try {
      const { data, error } = await supabase.functions.invoke('create-checkout-session', {
        body: {
          planId,
          billingCycle
        }
      });

      if (error) {
        console.error('Error creating checkout session:', error);
        return { success: false, error: error.message };
      }

      // Extract the actual data from the Edge Function response
      if (data && data.success && data.data) {
        return { success: true, data: data.data }; // Extract the nested data
      } else if (data && data.error) {
        return { success: false, error: data.error };
      } else {
        console.error('Unexpected Edge Function response format:', data);
        return { success: false, error: 'Invalid response format from payment service' };
      }
    } catch (error) {
      console.error('Error in createCheckoutSession:', error);
      return { success: false, error: 'Failed to create checkout session' };
    }
  }

  /**
   * Create a Stripe customer portal session for subscription management
   * @param {string} organizationId - The organization ID
   * @returns {Promise<{success: boolean, data?: object, error?: string}>}
   */
  async createPortalSession(organizationId) {
    try {
      const { data, error } = await supabase.functions.invoke('create-portal-session', {
        body: {}
      });

      if (error) {
        console.error('Error creating portal session:', error);
        return { success: false, error: error.message };
      }

      // Extract the actual data from the Edge Function response
      if (data && data.success && data.data) {
        return { success: true, data: data.data }; // Extract the nested data
      } else if (data && data.error) {
        return { success: false, error: data.error };
      } else {
        console.error('Unexpected Edge Function response format:', data);
        return { success: false, error: 'Invalid response format from portal service' };
      }
    } catch (error) {
      console.error('Error in createPortalSession:', error);
      return { success: false, error: 'Failed to create portal session' };
    }
  }

  /**
   * Cancel a subscription (downgrade to free)
   * @param {string} organizationId - The organization ID
   * @returns {Promise<{success: boolean, data?: object, error?: string}>}
   */
  async cancelSubscription(organizationId) {
    try {
      const { data, error } = await supabase.functions.invoke('cancel-subscription', {
        body: {}
      });

      if (error) {
        console.error('Error canceling subscription:', error);
        return { success: false, error: error.message };
      }

      // Extract the actual data from the Edge Function response
      if (data && data.success && data.data) {
        return { success: true, data: data.data };
      } else if (data && data.error) {
        return { success: false, error: data.error };
      } else {
        console.error('Unexpected Edge Function response format:', data);
        return { success: false, error: 'Invalid response format from subscription service' };
      }
    } catch (error) {
      console.error('Error in cancelSubscription:', error);
      return { success: false, error: 'Failed to cancel subscription' };
    }
  }

  /**
   * Get Stripe customer and subscription info
   * @param {string} organizationId - The organization ID
   * @returns {Promise<{success: boolean, data?: object, error?: string}>}
   */
  async getStripeInfo(organizationId) {
    try {
      const { data, error } = await supabase.functions.invoke('get-stripe-info', {
        body: { organizationId }
      });

      if (error) {
        console.error('Error getting Stripe info:', error);
        return { success: false, error: error.message };
      }

      // Extract the actual data from the Edge Function response
      if (data && data.success && data.data) {
        return { success: true, data: data.data };
      } else if (data && data.error) {
        return { success: false, error: data.error };
      } else {
        console.error('Unexpected Edge Function response format:', data);
        return { success: false, error: 'Invalid response format from Stripe info service' };
      }
    } catch (error) {
      console.error('Error in getStripeInfo:', error);
      return { success: false, error: 'Failed to get Stripe information' };
    }
  }

  /**
   * Update payment method
   * @param {string} organizationId - The organization ID
   * @param {string} paymentMethodId - Stripe payment method ID
   * @returns {Promise<{success: boolean, data?: object, error?: string}>}
   */
  async updatePaymentMethod(organizationId, paymentMethodId) {
    try {
      const { data, error } = await supabase.functions.invoke('update-payment-method', {
        body: {
          organizationId,
          paymentMethodId
        }
      });

      if (error) {
        console.error('Error updating payment method:', error);
        return { success: false, error: error.message };
      }

      // Extract the actual data from the Edge Function response
      if (data && data.success && data.data) {
        return { success: true, data: data.data };
      } else if (data && data.error) {
        return { success: false, error: data.error };
      } else {
        console.error('Unexpected Edge Function response format:', data);
        return { success: false, error: 'Invalid response format from payment method service' };
      }
    } catch (error) {
      console.error('Error in updatePaymentMethod:', error);
      return { success: false, error: 'Failed to update payment method' };
    }
  }

  /**
   * Redirect to Stripe Checkout
   * @param {string} sessionId - Stripe checkout session ID
   */
  redirectToCheckout(sessionId) {
    // This would use Stripe.js in a real implementation
    // For now, we'll handle this via the Edge Function response
    console.log('Redirecting to Stripe Checkout:', sessionId);
  }

  /**
   * Redirect to Stripe Customer Portal
   * @param {string} sessionUrl - Portal session URL
   */
  redirectToPortal(sessionUrl) {
    window.location.href = sessionUrl;
  }

  /**
   * Handle upgrade to Pro plan
   * @param {string} organizationId - The organization ID
   * @param {string} billingCycle - 'monthly' or 'yearly'
   * @returns {Promise<{success: boolean, data?: object, error?: string}>}
   */
  async upgradeToPro(organizationId, billingCycle = 'monthly') {
    try {
      // Get Pro plan ID
      const proResult = await subscriptionService.getProPlan();
      
      if (!proResult.success) {
        return { success: false, error: 'Failed to get Pro plan details' };
      }

      // Create checkout session
      const checkoutResult = await this.createCheckoutSession(
        organizationId, 
        proResult.data.id, 
        billingCycle
      );

      if (!checkoutResult.success) {
        return checkoutResult;
      }

      // Redirect to Stripe Checkout
      if (checkoutResult.data.url) {
        window.location.href = checkoutResult.data.url;
      }

      return { success: true, data: checkoutResult.data };
    } catch (error) {
      console.error('Error in upgradeToPro:', error);
      return { success: false, error: 'Failed to start upgrade process' };
    }
  }

  /**
   * Handle downgrade to Free plan
   * @param {string} organizationId - The organization ID
   * @returns {Promise<{success: boolean, data?: object, error?: string}>}
   */
  async downgradeToFree(organizationId) {
    try {
      // Cancel Stripe subscription and update local records
      const cancelResult = await this.cancelSubscription(organizationId);
      
      if (!cancelResult.success) {
        return cancelResult;
      }

      // Update local subscription record
      const downgradeResult = await subscriptionService.downgradeToFree(organizationId);
      
      return downgradeResult;
    } catch (error) {
      console.error('Error in downgradeToFree:', error);
      return { success: false, error: 'Failed to downgrade to free plan' };
    }
  }

  /**
   * Open Stripe Customer Portal for subscription management
   * @param {string} organizationId - The organization ID
   * @returns {Promise<{success: boolean, data?: object, error?: string}>}
   */
  async openCustomerPortal(organizationId) {
    try {
      const portalResult = await this.createPortalSession(organizationId);
      
      if (!portalResult.success) {
        return portalResult;
      }

      // Redirect to customer portal
      if (portalResult.data.url) {
        this.redirectToPortal(portalResult.data.url);
      }

      return { success: true, data: portalResult.data };
    } catch (error) {
      console.error('Error in openCustomerPortal:', error);
      return { success: false, error: 'Failed to open customer portal' };
    }
  }

  /**
   * Get pricing information for display
   * @returns {object} Pricing display data
   */
  getPricingDisplay() {
    return {
      free: {
        name: 'Free Plan',
        price: '$0',
        period: 'forever',
        features: [
          '2 AI reviews per month',
          'All core features',
          'Email support'
        ]
      },
      pro: {
        name: 'Pro Plan',
        monthly: {
          price: '$97',
          period: 'per month',
          annual_savings: null
        },
        yearly: {
          price: '$597',
          period: 'per year',
          annual_savings: '$567 saved'
        },
        features: [
          'Unlimited AI reviews',
          'All core features',
          'Priority support',
          'Custom integrations'
        ]
      }
    };
  }

  /**
   * Calculate savings for yearly billing
   * @param {number} monthlyPrice - Monthly price
   * @param {number} yearlyPrice - Yearly price
   * @returns {object} Savings calculation
   */
  calculateYearlySavings(monthlyPrice, yearlyPrice) {
    const monthlyTotal = monthlyPrice * 12;
    const savings = monthlyTotal - yearlyPrice;
    const savingsPercentage = Math.round((savings / monthlyTotal) * 100);
    
    return {
      savings,
      savingsPercentage,
      monthlyTotal,
      yearlyPrice
    };
  }
}

export default new StripeService();